import {
  ComponentType,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import styled, { css } from 'styled-components';

import { CloseIcon, SearchIcon } from '../icons';
import { THIN_SCROLLBAR } from '../../styles/scrollbar';

import { useAgentActions, useAgentState } from '../../providers/contexts';
import { useTerminalConfig } from '../../context/config-context';
import { CredentialsSection } from '../settings/CredentialsSection';
import { looseIncludes } from '../search-match';
import { normalizeSkillStatusPayload, type SkillStatus } from './skill-status';
import ParadigmLogo from './logos/ParadigmLogo';
import ParadexLogo from './logos/ParadexLogo';

/** A skill row narrowed to the fields the list renders. */
interface SkillRow {
  readonly name: string;
  readonly description: string;
  readonly enabled: boolean;
  readonly disabledReason: string | null;
}

function toRow(skill: SkillStatus): SkillRow | null {
  if (typeof skill.name !== 'string' || skill.name.length === 0) {
    return null;
  }
  const description =
    typeof skill.description === 'string' && skill.description.length > 0
      ? skill.description
      : 'No description provided.';
  // A skill is usable only when it's both enabled and eligible. Either being
  // explicitly false greys it out; absent flags are treated as available.
  const enabled = skill.enabled !== false && skill.eligible !== false;
  return {
    name: skill.name,
    description,
    enabled,
    disabledReason: describeDisabled(skill),
  };
}

/** Human-readable reason a skill is unavailable, or null when it's usable. */
function describeDisabled(skill: SkillStatus): string | null {
  if (typeof skill.disabledReason === 'string' && skill.disabledReason.length) {
    return skill.disabledReason;
  }
  if (Array.isArray(skill.missingRequirements)) {
    const reqs = skill.missingRequirements.filter(
      (r): r is string => typeof r === 'string' && r.length > 0,
    );
    if (reqs.length > 0) {
      return `Missing: ${reqs.join(', ')}`;
    }
  }
  if (skill.eligible === false) return 'Requirements not met.';
  if (skill.enabled === false) return 'Disabled.';
  return null;
}

type LoadState =
  | { readonly kind: 'loading' }
  | { readonly kind: 'error' }
  | { readonly kind: 'ready'; readonly skills: readonly SkillRow[] };

/**
 * Apps the gateway can surface skills from. Each app carries the metadata
 * the redesigned browse + detail views render: a short tagline for the
 * card grid, a longer paragraph + developer credit for the detail page.
 * Skills are matched to an app via a case-insensitive prefix boundary
 * check (see `skillBelongsToApp`).
 */
interface AppDef {
  readonly id: string;
  readonly name: string;
  readonly tagline: string;
  readonly description: string;
  readonly developer: string;
  readonly developerUrl?: string;
  readonly prefix: string;
  readonly Logo: ComponentType<{ readonly size?: number }>;
  /** Logo tint for the icon tile. */
  readonly tint: string;
}

const APPS: readonly AppDef[] = [
  {
    id: 'paradigm',
    name: 'Paradigm',
    tagline: 'Institutional block trades and RFQ workflows.',
    description:
      'Paradigm tools let the agent originate and manage institutional ' +
      'block trades — discovering RFQs, pricing legs, and executing on ' +
      'connected venues from inside the chat. Skills here cover the full ' +
      'lifecycle from quoting to settlement.',
    developer: 'Paradigm',
    developerUrl: 'https://www.paradigm.co',
    prefix: 'paradigm',
    Logo: ParadigmLogo,
    tint: '#5f74ff',
  },
  {
    id: 'paradex',
    name: 'Paradex',
    tagline: '24/7 Futures and Options Trading with Privacy',
    description:
      'Paradex tools let the agent operate inside the Paradex perpetuals ' +
      'exchange: reading markets, balances, positions and funding, ' +
      'screening setups, monitoring risk, and submitting orders on the ' +
      "user's behalf with confirmation gates.",
    developer: 'Paradex',
    developerUrl: 'https://www.paradex.trade',
    prefix: 'paradex',
    Logo: ParadexLogo,
    tint: 'var(--text-primary)',
  },
];

// Boundary match: a skill belongs to an app only when its name equals the
// prefix or is followed by a `-`/`.` separator — so `paradigmatic-foo` does
// NOT fall under `paradigm`.
function skillBelongsToApp(skill: SkillRow, app: AppDef): boolean {
  const name = skill.name.toLowerCase();
  const p = app.prefix.toLowerCase();
  return name === p || name.startsWith(`${p}-`) || name.startsWith(`${p}.`);
}

// Synthetic bucket id for skills that match no configured app — surfaced so
// nothing the agent advertises silently disappears from the UI.
const OTHER_APP_ID = 'other';

interface AppGroup {
  readonly id: string;
  readonly name: string;
  readonly tagline: string;
  readonly description: string;
  readonly developer: string;
  readonly developerUrl?: string;
  readonly app: AppDef | null;
  readonly skills: readonly SkillRow[];
}

/** Stable empty reference so memo deps don't churn before skills load. */
const EMPTY_SKILLS: readonly SkillRow[] = [];

/**
 * LocalStorage key for the user's per-app uninstall set. We store
 * UNINSTALLED ids (not installed ids) so the default — empty set ⇒ every
 * known app is installed — matches the gateway's behaviour today, where
 * every advertised skill is available without an explicit opt-in.
 *
 * TODO(openclaw): the uninstall toggle is UI-only for now — it only hides
 * the app + its skills from this view. The gateway still advertises every
 * skill and the agent will still fire them. When openclaw grows a real
 * per-user installed set (opt-in on the gateway), delete this
 * localStorage plumbing and drive `installed` off that source.
 */
const UNINSTALLED_APPS_KEY = 'dime-terminal:uninstalled-apps';

function readUninstalledAppIds(): ReadonlySet<string> {
  if (typeof window === 'undefined') return new Set();
  try {
    const raw = window.localStorage.getItem(UNINSTALLED_APPS_KEY);
    if (raw == null || raw.length === 0) return new Set();
    const parsed: unknown = JSON.parse(raw);
    if (!Array.isArray(parsed)) return new Set();
    return new Set(parsed.filter((v): v is string => typeof v === 'string'));
  } catch {
    return new Set();
  }
}

function writeUninstalledAppIds(ids: ReadonlySet<string>): void {
  if (typeof window === 'undefined') return;
  try {
    window.localStorage.setItem(UNINSTALLED_APPS_KEY, JSON.stringify([...ids]));
  } catch (err) {
    // localStorage unavailable (private mode, quota). Uninstall state stays
    // in-memory for the current tab; warn once so the lost-on-refresh
    // behaviour isn't silent.
    // eslint-disable-next-line no-console
    console.warn('[SkillsView] could not persist app install state', err);
  }
}

/**
 * LocalStorage cache of the last `skills.status` payload. Lets reopening
 * the Apps & Skills tab paint instantly off the cache while a background
 * refresh fetches the current set; the gateway response replaces the cache
 * once it arrives. First-ever open is still a one-shot "Loading skills…".
 */
const SKILLS_CACHE_KEY = 'dime-terminal:skills-cache';

function readSkillsCache(): readonly SkillRow[] | null {
  if (typeof window === 'undefined') return null;
  try {
    const raw = window.localStorage.getItem(SKILLS_CACHE_KEY);
    if (raw == null || raw.length === 0) return null;
    const parsed: unknown = JSON.parse(raw);
    if (!Array.isArray(parsed)) return null;
    // Re-validate each cached row so a stale schema doesn't crash render.
    const rows: SkillRow[] = [];
    for (const entry of parsed) {
      if (entry == null || typeof entry !== 'object') continue;
      const r = entry as Record<string, unknown>;
      if (typeof r.name !== 'string' || r.name.length === 0) continue;
      rows.push({
        name: r.name,
        description:
          typeof r.description === 'string'
            ? r.description
            : 'No description provided.',
        enabled: typeof r.enabled === 'boolean' ? r.enabled : true,
        disabledReason:
          typeof r.disabledReason === 'string' ? r.disabledReason : null,
      });
    }
    return rows;
  } catch {
    return null;
  }
}

function writeSkillsCache(skills: readonly SkillRow[]): void {
  if (typeof window === 'undefined') return;
  try {
    window.localStorage.setItem(SKILLS_CACHE_KEY, JSON.stringify(skills));
  } catch {
    /* localStorage unavailable — refresh-on-next-open is the fallback. */
  }
}

// Credentials sits beside All/Installed rather than in Settings, and the
// grouping is the argument: a credential is what an app is allowed to do on
// your behalf, so it belongs with the apps, not with theme and language. It is
// the only tab whose content is not a view of `skills.status`.
type Tab = 'all' | 'installed' | 'credentials';

type View =
  | { readonly kind: 'list' }
  | { readonly kind: 'app'; readonly appId: string }
  | {
      readonly kind: 'skill';
      readonly appId: string;
      readonly skillName: string;
    };

/**
 * "Apps & Skills" tab. Lists skills the gateway advertises via the
 * `skills.status` RPC. UI mirrors Claude's connector browser: a search bar +
 * All/Installed tabs above a 2-col app card grid. Selecting an app opens a
 * detail page (description + developer + skills-as-pills); each skill pill
 * opens its own detail view.
 */
export default function SkillsView() {
  const actions = useAgentActions();
  const state = useAgentState();
  const isReady = state.status === 'authenticated';

  // Initialize from the persisted cache so reopening this tab paints
  // instantly with the last known skills set. A background `skills.status`
  // fetch still runs to refresh the data; only a cold first-ever open
  // shows the "Loading skills…" banner.
  const [load, setLoad] = useState<LoadState>(() => {
    const cached = readSkillsCache();
    return cached != null && cached.length > 0
      ? { kind: 'ready', skills: cached }
      : { kind: 'loading' };
  });
  const [view, setView] = useState<View>({ kind: 'list' });
  const [searchQuery, setSearchQuery] = useState('');
  const [activeTab, setActiveTab] = useState<Tab>('all');
  // A backend capability, not a preference: credentials need the DIME web-api
  // behind them, so an embed whose host supplies the gateway directly must not
  // see the tab at all. Hiding it beats offering one that can only explain why
  // it does nothing.
  const credentialsEnabled = useTerminalConfig().credentials != null;
  // User-controlled install state per app id. Stored as the negative set
  // (uninstalled ids) so a fresh user with no localStorage entry sees every
  // app installed — matching the gateway's "everything advertised is
  // available" behaviour today. See [UNINSTALLED_APPS_KEY] — this is UI-only
  // filtering until the gateway grows a real per-user installed set.
  const [uninstalledAppIds, setUninstalledAppIds] = useState<
    ReadonlySet<string>
  >(() => readUninstalledAppIds());

  const setAppInstalled = useCallback((appId: string, installed: boolean) => {
    setUninstalledAppIds((prev) => {
      const next = new Set(prev);
      if (installed) {
        next.delete(appId);
      } else {
        next.add(appId);
      }
      writeUninstalledAppIds(next);
      return next;
    });
  }, []);

  const isAppInstalled = useCallback(
    (appId: string) => !uninstalledAppIds.has(appId),
    [uninstalledAppIds],
  );

  // Cross-tab sync for the install toggle: when another tab writes the
  // uninstalled set to localStorage, the `storage` event fires here so this
  // tab's in-memory state catches up. Without this, toggling Install in one
  // tab leaves the other tab's All/Installed filter out of date.
  useEffect(() => {
    if (typeof window === 'undefined') return undefined;
    function onStorage(event: StorageEvent) {
      if (event.key !== UNINSTALLED_APPS_KEY) return;
      setUninstalledAppIds(readUninstalledAppIds());
    }
    window.addEventListener('storage', onStorage);
    return () => window.removeEventListener('storage', onStorage);
  }, []);

  // Single source of truth for refreshing the skill catalog. Extracted so
  // both the auth-state-change effect and the tab-visibility listener fire
  // through the same path. Returns the cancellation flag the caller uses to
  // discard a stale response if the component unmounts mid-flight.
  const fetchSkills = useCallback((): { cancel: () => void } => {
    let cancelled = false;
    setLoad((prev) => (prev.kind === 'ready' ? prev : { kind: 'loading' }));
    actions
      .request<unknown>('skills.status')
      .then((payload) => {
        if (cancelled) return;
        const skills = normalizeSkillStatusPayload(payload)
          .map(toRow)
          .filter((row): row is SkillRow => row != null)
          // Usable skills first, then alphabetical within each group.
          .sort((a, b) => {
            if (a.enabled !== b.enabled) return a.enabled ? -1 : 1;
            return a.name.localeCompare(b.name);
          });
        setLoad({ kind: 'ready', skills });
        writeSkillsCache(skills);
      })
      .catch(() => {
        if (cancelled) return;
        // Keep the cached snapshot rather than blanking to `error` when we
        // have something useful to show. The user gets stale data + no
        // banner instead of a usable cache hidden behind an error message.
        setLoad((prev) => (prev.kind === 'ready' ? prev : { kind: 'error' }));
      });
    return {
      cancel: () => {
        cancelled = true;
      },
    };
  }, [actions]);

  useEffect(() => {
    if (!isReady) {
      // Don't downgrade a cached `ready` snapshot to `loading` just because
      // the connection isn't authenticated yet — the cached list stays
      // useful while the WS reconnects.
      setLoad((prev) => (prev.kind === 'ready' ? prev : { kind: 'loading' }));
      return undefined;
    }
    const handle = fetchSkills();
    return handle.cancel;
  }, [isReady, fetchSkills]);

  // Refresh on tab visibility — covers the "user came back to the tab and
  // the gateway has new skills" gap. Without this the cache could sit
  // stale indefinitely as long as `isReady` stays true. We only refresh
  // when both the page is visible AND the agent is authenticated.
  //
  // Doesn't share a `cancelled` flag with the auth-state effect's in-flight
  // fetch, so a slow first fetch + a fast tab toggle can race two
  // `skills.status` requests. The later response wins via `setLoad`
  // ordering — both paths normalize and sort identically, so the race is
  // benign (worst case: an extra round-trip).
  useEffect(() => {
    if (typeof document === 'undefined') return undefined;
    function onVisibilityChange() {
      if (document.visibilityState !== 'visible') return;
      if (!isReady) return;
      fetchSkills();
    }
    document.addEventListener('visibilitychange', onVisibilityChange);
    return () => {
      document.removeEventListener('visibilitychange', onVisibilityChange);
    };
  }, [isReady, fetchSkills]);

  // Only show live skills the gateway has actually returned. When
  // disconnected / loading / errored, the status banner takes over and the
  // grid stays empty — no demo content.
  const allSkills: readonly SkillRow[] =
    load.kind === 'ready' ? load.skills : EMPTY_SKILLS;

  // Group skills by app, plus an "Other" bucket for anything matching no
  // configured prefix.
  const groups = useMemo<readonly AppGroup[]>(() => {
    const byApp: AppGroup[] = APPS.map((app) => ({
      id: app.id,
      name: app.name,
      tagline: app.tagline,
      description: app.description,
      developer: app.developer,
      developerUrl: app.developerUrl,
      app,
      skills: allSkills.filter((s) => skillBelongsToApp(s, app)),
    }));
    const other = allSkills.filter(
      (s) => !APPS.some((app) => skillBelongsToApp(s, app)),
    );
    if (other.length > 0) {
      byApp.push({
        id: OTHER_APP_ID,
        name: 'Other',
        tagline: 'Skills not tied to a known app.',
        description:
          'Everything the gateway advertises that does not match a ' +
          'known app prefix lands here so nothing is silently dropped.',
        developer: 'Unknown',
        app: null,
        skills: other,
      });
    }
    return byApp;
  }, [allSkills]);

  // Filter pipeline: tab → search.
  // "Installed" = apps the user hasn't toggled off in the detail page.
  // "All" = every app the terminal knows about (Paradigm, Paradex, plus the
  // synthetic Other bucket when something doesn't match), even when its
  // skill count is zero, so users can see the catalog and the empty state.
  const visibleGroups = useMemo(() => {
    const tabFiltered =
      activeTab === 'installed'
        ? groups.filter((g) => isAppInstalled(g.id))
        : groups;
    const q = searchQuery.trim();
    if (q.length === 0) return tabFiltered;
    return tabFiltered.filter((g) => {
      if (looseIncludes(g.name, q)) return true;
      if (looseIncludes(g.tagline, q)) return true;
      return g.skills.some(
        (s) => looseIncludes(s.name, q) || looseIncludes(s.description, q),
      );
    });
  }, [groups, activeTab, searchQuery, isAppInstalled]);

  // Second-level search hits: individual skills whose name or description
  // matches the query. Lets the user jump straight into a skill detail page
  // instead of going through its parent app card first. Only computed while
  // a query is active; the "Installed" tab respects the user's per-app
  // install state so uninstalled apps' skills don't pollute the results.
  const skillSearchResults = useMemo(() => {
    const q = searchQuery.trim();
    if (q.length === 0) return [];
    return groups
      .filter((g) => activeTab === 'all' || isAppInstalled(g.id))
      .flatMap((g) =>
        g.skills
          .filter(
            (s) => looseIncludes(s.name, q) || looseIncludes(s.description, q),
          )
          .map((s) => ({ group: g, skill: s })),
      );
  }, [groups, searchQuery, activeTab, isAppInstalled]);

  const selectedGroup =
    view.kind !== 'list'
      ? (groups.find((g) => g.id === view.appId) ?? null)
      : null;
  const selectedSkill =
    view.kind === 'skill' && selectedGroup != null
      ? (selectedGroup.skills.find((s) => s.name === view.skillName) ?? null)
      : null;

  // Snap back to the list when the gateway pushes an update that removes the
  // app or skill the user is currently viewing — otherwise both detail
  // branches return null and we'd render the list view sans breadcrumb,
  // which reads as "the page glitched". Reset here so the user lands on a
  // coherent surface (the list) instead of in a broken detail view.
  useEffect(() => {
    if (view.kind === 'app' && selectedGroup == null) {
      setView({ kind: 'list' });
    } else if (
      view.kind === 'skill' &&
      (selectedGroup == null || selectedSkill == null)
    ) {
      setView({ kind: 'list' });
    }
  }, [view, selectedGroup, selectedSkill]);

  // Connection / load state — surfaced as an inline banner so the catalog UI
  // (search, tabs, cards) is always visible even when offline or mid-load.
  // The card grid is built from the static APP catalog, so it renders with
  // zero skills attached when disconnected; the user can browse the layout
  // and still navigate into an app detail page to read descriptions.
  // Names the field for what the CURRENT tab searches. The query itself is
  // deliberately shared across tabs rather than reset: All and Installed
  // already share one, and text vanishing out of a box you can see is worse
  // than a filter you can see the reason for — the empty states below name the
  // query when it is what emptied them.
  const searchLabel =
    activeTab === 'credentials'
      ? 'Search credentials'
      : 'Search apps and skills';

  const statusBanner = !isReady
    ? 'Connect to the agent to load live skill status.'
    : load.kind === 'loading'
      ? 'Loading skills…'
      : load.kind === 'error'
        ? "Couldn't load skills from the gateway."
        : null;

  // ---- Skill detail ----
  if (view.kind === 'skill' && selectedGroup != null && selectedSkill != null) {
    return (
      <SkillDetail
        group={selectedGroup}
        skill={selectedSkill}
        onBack={() => setView({ kind: 'app', appId: selectedGroup.id })}
      />
    );
  }

  // ---- App detail ----
  if (view.kind === 'app' && selectedGroup != null) {
    return (
      <AppDetail
        group={selectedGroup}
        onBack={() => setView({ kind: 'list' })}
        onSelectSkill={(skillName) =>
          setView({ kind: 'skill', appId: selectedGroup.id, skillName })
        }
      />
    );
  }

  // ---- List view ----
  return (
    <Scroll>
      <PageTitle>Apps &amp; Skills</PageTitle>
      {/* ONE search field, in one place, on every tab. It used to be hidden
          on Credentials, which cost that tab its only way to find a name in a
          long list and — because the field sits ABOVE the tab strip — moved
          the tabs up and down as you switched between them. What it searches
          follows the tab, so its name does too rather than staying generic:
          the field is a control, and a control that says "apps and skills"
          while filtering credentials is lying about itself. */}
      <SearchWrap>
        <FieldSearchIcon />
        <SearchInput
          type="search"
          placeholder={searchLabel}
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          onKeyDown={(e) => {
            // Escape clears the search (matches Claude's connector browser).
            if (e.key === 'Escape' && searchQuery.length > 0) {
              e.preventDefault();
              setSearchQuery('');
            }
          }}
          aria-label={searchLabel}
        />
        {searchQuery.length > 0 && (
          <SearchClearButton
            type="button"
            aria-label="Clear search"
            title="Clear search"
            onClick={() => setSearchQuery('')}
          >
            <CloseIcon size={12} />
          </SearchClearButton>
        )}
      </SearchWrap>

      <Tabs role="tablist">
        <Tab
          type="button"
          role="tab"
          aria-selected={activeTab === 'all'}
          $active={activeTab === 'all'}
          onClick={() => setActiveTab('all')}
        >
          All
        </Tab>
        <Tab
          type="button"
          role="tab"
          aria-selected={activeTab === 'installed'}
          $active={activeTab === 'installed'}
          onClick={() => setActiveTab('installed')}
        >
          Installed
        </Tab>
        {credentialsEnabled && (
          <Tab
            type="button"
            role="tab"
            aria-selected={activeTab === 'credentials'}
            $active={activeTab === 'credentials'}
            onClick={() => setActiveTab('credentials')}
          >
            Credentials
          </Tab>
        )}
      </Tabs>

      {activeTab === 'credentials' && credentialsEnabled && (
        <CredentialsPane>
          <CredentialsSection query={searchQuery} />
        </CredentialsPane>
      )}

      {activeTab !== 'credentials' && statusBanner != null && (
        <StatusText>{statusBanner}</StatusText>
      )}

      {activeTab !== 'credentials' && visibleGroups.length > 0 && (
        <AppGrid>
          {visibleGroups.map((group) => {
            const installed = isAppInstalled(group.id);
            return (
              <AppCard key={group.id}>
                <AppCardMain
                  type="button"
                  onClick={() => setView({ kind: 'app', appId: group.id })}
                >
                  <AppCardIcon
                    style={{ color: group.app?.tint ?? 'var(--text-label)' }}
                  >
                    {group.app != null ? (
                      <group.app.Logo size={28} />
                    ) : (
                      <OtherGlyph>⋯</OtherGlyph>
                    )}
                  </AppCardIcon>
                  <AppCardBody>
                    <AppCardName>{group.name}</AppCardName>
                    <AppCardTagline>{group.tagline}</AppCardTagline>
                    <AppCardMeta>
                      {group.skills.length}{' '}
                      {group.skills.length === 1 ? 'skill' : 'skills'}
                    </AppCardMeta>
                  </AppCardBody>
                </AppCardMain>
                <InstallToggle
                  type="button"
                  role="switch"
                  aria-checked={installed}
                  // Stable, app-specific name — a switch's accessible name
                  // should identify what it controls, not restate its
                  // current state (that's `aria-checked`'s job). Every row
                  // sharing "Install app" made them indistinguishable when
                  // tabbing through the list with a screen reader.
                  aria-label={`Install ${group.name}`}
                  $checked={installed}
                  onClick={() => setAppInstalled(group.id, !installed)}
                />
              </AppCard>
            );
          })}
        </AppGrid>
      )}

      {/* Second-level search results: individual matching skills, surfaced
          only while a query is active. Click jumps straight into the skill
          detail page. */}
      {activeTab !== 'credentials' && skillSearchResults.length > 0 && (
        <>
          <SectionHeader>
            Skills
            <SectionCount>{skillSearchResults.length}</SectionCount>
          </SectionHeader>
          <PillRow>
            {skillSearchResults.map(({ group, skill }) => (
              <SkillPill
                key={`${group.id}:${skill.name}`}
                type="button"
                $disabled={!skill.enabled}
                onClick={() =>
                  setView({
                    kind: 'skill',
                    appId: group.id,
                    skillName: skill.name,
                  })
                }
              >
                {skill.name}
              </SkillPill>
            ))}
          </PillRow>
        </>
      )}

      {activeTab !== 'credentials' &&
        visibleGroups.length === 0 &&
        skillSearchResults.length === 0 && (
          <StatusText>
            {searchQuery.trim().length > 0
              ? `No apps or skills match "${searchQuery.trim()}".`
              : 'No apps to show.'}
          </StatusText>
        )}
    </Scroll>
  );
}

// ─────────────────────────────────────────────────────────────────────────
// App detail
// ─────────────────────────────────────────────────────────────────────────

function AppDetail({
  group,
  onBack,
  onSelectSkill,
}: {
  readonly group: AppGroup;
  readonly onBack: () => void;
  readonly onSelectSkill: (skillName: string) => void;
}) {
  return (
    <Scroll>
      <BackLink type="button" onClick={onBack}>
        ‹ Back
      </BackLink>

      <DetailHeader>
        <DetailIcon style={{ color: group.app?.tint ?? 'var(--text-label)' }}>
          {group.app != null ? (
            <group.app.Logo size={36} />
          ) : (
            <OtherGlyph>⋯</OtherGlyph>
          )}
        </DetailIcon>
        <DetailHeaderText>
          <DetailTitle>{group.name}</DetailTitle>
          <DetailTagline>{group.tagline}</DetailTagline>
        </DetailHeaderText>
      </DetailHeader>

      <DetailDescription>{group.description}</DetailDescription>

      <DetailMetaRow>
        <DetailMetaLabel>Developed by</DetailMetaLabel>
        {group.developerUrl != null ? (
          <DetailMetaLink
            href={group.developerUrl}
            target="_blank"
            rel="noopener noreferrer"
          >
            {group.developer}
          </DetailMetaLink>
        ) : (
          <DetailMetaValue>{group.developer}</DetailMetaValue>
        )}
      </DetailMetaRow>

      <SectionHeader>
        Skills
        <SectionCount>{group.skills.length}</SectionCount>
      </SectionHeader>

      {group.skills.length === 0 ? (
        <Notice>No skills installed for {group.name}.</Notice>
      ) : (
        <PillRow>
          {group.skills.map((skill) => (
            <SkillPill
              key={skill.name}
              type="button"
              $disabled={!skill.enabled}
              onClick={() => onSelectSkill(skill.name)}
            >
              {skill.name}
            </SkillPill>
          ))}
        </PillRow>
      )}
    </Scroll>
  );
}

// ─────────────────────────────────────────────────────────────────────────
// Skill detail
// ─────────────────────────────────────────────────────────────────────────

interface ParsedSkillContent {
  /** Capability/summary sentences before the "Use this skill…" guidance. */
  readonly overview: string;
  /** When-to-use guidance with quoted phrases stripped. */
  readonly whenToUse: string;
  /** Quoted trigger phrases extracted from the full description, dedup'd. */
  readonly triggerPhrases: readonly string[];
}

/**
 * Pull a SKILL.md-style description apart into three sections so every
 * skill renders against the same template (overview → chips → when-to-use),
 * with no quoted phrase ever appearing in both the prose and the chips.
 *
 * Heuristics (defensive — anything we can't parse still ends up in the
 * overview):
 *  - **Trigger phrases**: every `"..."` substring across the description.
 *    Extracted FIRST so we can strip them out of all prose sections,
 *    eliminating the "phrase shows up in chips AND in the paragraph"
 *    duplication the older parser left in place.
 *  - **Split point**: first sentence starting with a routing verb —
 *    "Use", "Trigger when", "Also trigger", "Routes here", "When to
 *    use". Broader than the previous "Use this skill / Use when" so
 *    descriptions phrased as "Use whenever…" or "Also trigger when…"
 *    split correctly.
 *  - **Overview**: sentences before the split point (or the whole thing
 *    if no split was found), quotes stripped + punctuation tidied.
 *  - **When to use**: trailing sentences from the split, same cleanup.
 */
/**
 * Remove quoted phrases (already shown as chips) from a prose paragraph and
 * mop up the punctuation left behind. Loops until idempotent so chains like
 * `— "a", "b", "c".` reduce cleanly to `.` instead of leaving `— , , , .`.
 */
function stripQuotesAndTidy(input: string): string {
  let prev: string;
  let next = input.replace(/"[^"]+"/g, '');
  do {
    prev = next;
    next = next
      .replace(/\s+/g, ' ')
      .replace(/,\s*,/g, ',')
      .replace(/,\s*\./g, '.')
      .replace(/—\s*,/g, '—')
      .replace(/—\s*\./g, '.')
      .replace(/—\s+([)\].,;:!?])/g, '$1')
      .replace(/—\s*$/g, '')
      .replace(/\(\s*\)/g, '')
      .replace(/\s+([,.;:!?])/g, '$1')
      .replace(/,\s*or\s*\./gi, '.')
      .replace(/,\s*or\s*,/gi, ',')
      .replace(/\s+/g, ' ')
      .trim();
  } while (prev !== next);
  return next;
}

function parseSkillContent(description: string): ParsedSkillContent {
  const triggerSet = new Set<string>();
  const triggerRe = /"([^"]+)"/g;
  let match: RegExpExecArray | null;
  while ((match = triggerRe.exec(description)) !== null) {
    const phrase = match[1].trim();
    if (phrase.length > 0) triggerSet.add(phrase);
  }

  // Split on sentence boundaries: a period (not part of an ellipsis), then
  // whitespace, then a capital letter. The capital-letter lookahead is what
  // keeps mid-sentence periods inside parens (e.g. "Paradigm's DRFQv2.")
  // and abbreviations (e.g. "e.g.", "i.e.") from shredding the description.
  const sentences = description
    .split(/(?<=[^.]\.)\s+(?=[A-Z])/)
    .filter((s) => s.trim().length > 0);
  const splitIdx = sentences.findIndex((s) =>
    /^(use\b|trigger\b|also trigger\b|routes? here\b|when to use\b)/i.test(
      s.trim(),
    ),
  );

  const overviewSentences =
    splitIdx > 0 ? sentences.slice(0, splitIdx) : sentences;
  const whenSentences = splitIdx > 0 ? sentences.slice(splitIdx) : [];

  const overview = stripQuotesAndTidy(overviewSentences.join(' '));
  const whenToUse = stripQuotesAndTidy(whenSentences.join(' '));

  return {
    overview: overview.length > 0 ? overview : stripQuotesAndTidy(description),
    whenToUse,
    triggerPhrases: [...triggerSet],
  };
}

function SkillDetail({
  group,
  skill,
  onBack,
}: {
  readonly group: AppGroup;
  readonly skill: SkillRow;
  readonly onBack: () => void;
}) {
  const parsed = useMemo(
    () => parseSkillContent(skill.description),
    [skill.description],
  );
  // Tracks which phrase chip was most recently copied so we can flip its label
  // to "Copied" for a short beat. One pending timer per detail page; cleared
  // on unmount so a fast back-nav doesn't leave a setState-on-unmount in
  // flight.
  const [copiedPhrase, setCopiedPhrase] = useState<string | null>(null);
  const copyHideTimerRef = useRef<number | null>(null);
  const copyPhrase = useCallback((phrase: string) => {
    // The clipboard API is unavailable on non-secure origins; fail silently
    // there — the chip still flashes "Copied" so the action feels acknowledged.
    void navigator.clipboard?.writeText(phrase);
    setCopiedPhrase(phrase);
    if (copyHideTimerRef.current != null) {
      window.clearTimeout(copyHideTimerRef.current);
    }
    copyHideTimerRef.current = window.setTimeout(() => {
      setCopiedPhrase((current) => (current === phrase ? null : current));
      copyHideTimerRef.current = null;
    }, 1200);
  }, []);
  useEffect(
    () => () => {
      if (copyHideTimerRef.current != null) {
        window.clearTimeout(copyHideTimerRef.current);
      }
    },
    [],
  );
  return (
    <Scroll>
      <BackLink type="button" onClick={onBack}>
        ‹ Back to {group.name}
      </BackLink>

      <DetailHeader>
        <DetailIcon style={{ color: group.app?.tint ?? 'var(--text-label)' }}>
          {group.app != null ? (
            <group.app.Logo size={36} />
          ) : (
            <OtherGlyph>⋯</OtherGlyph>
          )}
        </DetailIcon>
        <DetailHeaderText>
          <DetailTitle>{skill.name}</DetailTitle>
          <DetailTagline>{group.name} skill</DetailTagline>
        </DetailHeaderText>
        {!skill.enabled && <DisabledBadge>disabled</DisabledBadge>}
      </DetailHeader>

      <DetailDescription>{parsed.overview}</DetailDescription>

      {parsed.triggerPhrases.length > 0 && (
        <>
          <SectionHeader>
            Example prompts
            <SectionCount>{parsed.triggerPhrases.length}</SectionCount>
          </SectionHeader>
          <PhraseList>
            {parsed.triggerPhrases.map((phrase) => {
              const isCopied = copiedPhrase === phrase;
              return (
                <PhraseChip
                  key={phrase}
                  type="button"
                  onClick={() => copyPhrase(phrase)}
                  aria-label={`Copy prompt: ${phrase}`}
                >
                  {phrase}
                  <CopiedToast role="status" $visible={isCopied}>
                    Copied
                  </CopiedToast>
                </PhraseChip>
              );
            })}
          </PhraseList>
        </>
      )}

      {parsed.whenToUse.length > 0 && (
        <>
          <SectionHeader>When to use</SectionHeader>
          <DetailDescription>{parsed.whenToUse}</DetailDescription>
        </>
      )}

      {!skill.enabled && skill.disabledReason != null && (
        <DisabledReason>{skill.disabledReason}</DisabledReason>
      )}
    </Scroll>
  );
}

// ─────────────────────────────────────────────────────────────────────────
// Styles
// ─────────────────────────────────────────────────────────────────────────

const Scroll = styled.div`
  flex: 1;
  min-height: 0;
  overflow-y: auto;
  padding: 20px 24px;
  display: flex;
  flex-direction: column;
  gap: 16px;
  max-width: 920px;
  width: 100%;
  margin: 0 auto;
  ${THIN_SCROLLBAR}
`;

const PageTitle = styled.h2`
  margin: 0;
  font-size: 16px;
  font-weight: 500;
  color: var(--text-primary);
`;

const SearchWrap = styled.div`
  position: relative;
  display: flex;
  align-items: center;
`;

/* Sits inside the field rather than beside it, and must not swallow the
   click that focuses the input. */
const FieldSearchIcon = styled(SearchIcon)`
  position: absolute;
  left: 14px;
  pointer-events: none;
`;

const SearchInput = styled.input`
  width: 100%;
  height: 44px;
  /* Right padding reserves space for the absolutely-positioned clear button
     when it's rendered, so the typed text never tucks underneath it. */
  padding: 0 40px 0 40px;
  border-radius: 8px;
  border: 1px solid var(--border-color);
  background: var(--input-bg-color);
  color: var(--text-primary);
  font-family: inherit;
  font-size: 13px;

  &::placeholder {
    color: var(--text-secondary);
    opacity: 0.5;
  }

  &:focus {
    outline: none;
    border-color: var(--text-secondary);
  }

  /* Hide the native clear button on WebKit so the icon-only search reads cleanly. */
  &::-webkit-search-cancel-button {
    -webkit-appearance: none;
    appearance: none;
  }
`;

const SearchClearButton = styled.button`
  position: absolute;
  right: 10px;
  appearance: none;
  cursor: pointer;
  background: transparent;
  border: none;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 24px;
  height: 24px;
  border-radius: 4px;
  color: var(--text-secondary);

  &:hover {
    color: var(--text-primary);
    background: var(--hover-bg);
  }

  &:focus-visible {
    outline: 1px solid var(--accent-color);
    outline-offset: 1px;
  }
`;

const Tabs = styled.div`
  display: flex;
  padding: 4px 0;
  align-self: flex-start;
`;

/**
 * Holds the credentials pane, which is lifted whole out of Settings.
 *
 * Narrower than the app grid on purpose: the grid wants the full 920px for two
 * columns of cards, while this is a form and a list of one-line rows, and a
 * text field stretched to 920px is harder to read, not easier. The pane brings
 * its own vertical rhythm, so this only sets the measure.
 */
const CredentialsPane = styled.div`
  display: flex;
  flex-direction: column;
  max-width: 620px;
  width: 100%;
`;

const Tab = styled.button<{ $active: boolean }>`
  appearance: none;
  cursor: pointer;
  border: none;
  /* Use the design-system mid-grey token (--button-color-dark = #282b2e)
     instead of an arbitrary white-overlay — keeps the active pill in the
     same shade family as buttons and dropdown surfaces. */
  background: ${(p) =>
    p.$active ? 'var(--button-color-dark)' : 'transparent'};
  color: ${(p) =>
    p.$active ? 'var(--text-primary)' : 'var(--text-secondary)'};
  font-size: 14px;
  font-weight: 500;
  padding: 6px 14px;
  border-radius: 999px;
  transition:
    background 0.12s ease,
    color 0.12s ease;

  &:hover {
    color: var(--text-primary);
  }
`;

const AppGrid = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

// Non-interactive row: it only lays out the two *real* interactive
// children (AppCardMain's nav button, InstallToggle's switch) side by side.
// Deliberately not a button/role="button" itself — a clickable wrapper
// around a nested <button> is an ARIA nested-interactive-widget anti-pattern
// that some screen readers won't expose consistently, so only the icon/text
// portion (AppCardMain) is focusable/clickable, and the toggle is a true
// sibling rather than nested inside another interactive element.
const AppCard = styled.div`
  width: 100%;
  display: flex;
  align-items: center;
  gap: 8px;
  padding-right: 16px;
  border-radius: 12px;
  background: color-mix(in srgb, var(--bg-color-container) 60%, transparent);
  transition: background 0.15s ease;

  &:hover {
    background: var(--bg-color-container);
  }
`;

const AppCardMain = styled.button`
  appearance: none;
  cursor: pointer;
  text-align: left;
  flex: 1;
  min-width: 0;
  display: flex;
  align-items: center;
  gap: 14px;
  padding: 12px 16px;
  border: none;
  border-radius: 12px;
  background: transparent;
  color: var(--text-primary);

  &:focus-visible {
    outline: 2px solid var(--accent-color);
    outline-offset: -2px;
  }
`;

const AppCardIcon = styled.div`
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 44px;
  height: 44px;
  border-radius: 10px;
  background: var(--bg-color-page);
  border: 1px solid var(--border-color);
`;

const AppCardBody = styled.div`
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

const AppCardName = styled.div`
  font-size: 14px;
  font-weight: 500;
  color: var(--text-primary);
`;

const AppCardTagline = styled.div`
  font-size: 12px;
  color: var(--text-secondary);
  line-height: 1.5;
`;

const AppCardMeta = styled.div`
  margin-top: 4px;
  font-size: 11px;
  color: var(--text-label);
`;

const OtherGlyph = styled.span`
  font-size: 24px;
  line-height: 1;
  color: var(--text-label);
`;

const BackLink = styled.button`
  appearance: none;
  cursor: pointer;
  border: none;
  background: transparent;
  color: var(--text-secondary);
  font-size: 12px;
  font-family: inherit;
  padding: 4px 0;
  align-self: flex-start;

  &:hover {
    color: var(--text-primary);
  }
`;

const DetailHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 14px;
`;

// Right-aligned install/uninstall slide-switch in the app detail header.
// Mirrors Paradex's Toggle (ui/paradex/src/components/common/Toggle.tsx):
// 36×20 track that flips to --accent-color when checked, with a knob in the
// page colour that slides to the right. Uses `::before` for the knob so a
// single styled <button role="switch"> covers both element and a11y
// semantics. The knob reads --bg-color-page, not --text-primary: it has to
// contrast with the *track* it sits on, so it follows the page rather than
// the body text (which on a light theme would paint it near-black).
const InstallToggle = styled.button<{ $checked: boolean }>`
  margin-left: auto;
  flex-shrink: 0;
  position: relative;
  appearance: none;
  cursor: pointer;
  width: 36px;
  height: 20px;
  padding: 0;
  border: none;
  border-radius: 999px;
  background: ${(p) =>
    p.$checked ? 'var(--accent-color)' : 'var(--border-color)'};
  transition: background 0.15s ease;

  &::before {
    content: '';
    position: absolute;
    top: 2px;
    left: 2px;
    width: 16px;
    height: 16px;
    border-radius: 50%;
    background: var(--bg-color-page);
    transform: ${(p) => (p.$checked ? 'translateX(16px)' : 'translateX(0)')};
    transition: transform 0.15s ease;
  }

  &:focus-visible {
    outline: 2px solid var(--accent-color);
    outline-offset: 2px;
  }
`;

const DetailIcon = styled.div`
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 56px;
  height: 56px;
  border-radius: 14px;
  background: var(--bg-color-page);
  border: 1px solid var(--border-color);
`;

const DetailHeaderText = styled.div`
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

const DetailTitle = styled.div`
  font-size: 18px;
  font-weight: 600;
  color: var(--text-primary);
`;

const DetailTagline = styled.div`
  font-size: 12px;
  color: var(--text-secondary);
  line-height: 1.5;
`;

const DetailDescription = styled.p`
  margin: 0;
  font-size: 13px;
  font-weight: 400;
  color: var(--text-primary);
  line-height: 1.6;
`;

const DetailMetaRow = styled.div`
  display: flex;
  align-items: baseline;
  gap: 6px;
  font-size: 12px;
`;

const DetailMetaLabel = styled.span`
  color: var(--text-secondary);
`;

const DetailMetaValue = styled.span`
  color: var(--text-primary);
  font-weight: 500;
`;

const DetailMetaLink = styled.a`
  color: var(--accent-color);
  text-decoration: underline;
  font-weight: 500;

  &:hover {
    filter: brightness(1.15);
  }
`;

const SectionHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 13px;
  font-weight: 600;
  color: var(--text-primary);
  margin-top: 4px;
`;

const SectionCount = styled.span`
  font-size: 11px;
  font-weight: 500;
  padding: 2px 8px;
  border-radius: 999px;
  background: var(--bg-color-container);
  color: var(--text-secondary);
`;

const PillRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
`;

const PILL_DISABLED = css`
  opacity: 0.5;
`;

const SkillPill = styled.button<{ $disabled: boolean }>`
  appearance: none;
  cursor: pointer;
  font-family: inherit;
  font-size: 12px;
  font-weight: 500;
  padding: 6px 12px;
  border-radius: 999px;
  border: 1px solid var(--border-color);
  background: var(--bg-color-container);
  color: var(--text-primary);
  transition:
    background 0.12s ease,
    border-color 0.12s ease;

  &:hover {
    background: var(--hover-bg);
    border-color: color-mix(in srgb, var(--text-secondary) 40%, transparent);
  }

  ${(p) => p.$disabled && PILL_DISABLED}
`;

// Trigger-phrase chips: read-only quoted snippets the agent uses to route a
// user message to this skill. Same overall pill geometry as SkillPill but
// non-interactive and slightly more muted so they don't compete visually
// with the navigation pills on the app detail page.
const PhraseList = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
`;

const PhraseChip = styled.button`
  position: relative;
  font-family: inherit;
  font-size: 12px;
  font-weight: 400;
  padding: 5px 12px;
  border-radius: 999px;
  border: none;
  background: var(--button-color-dark);
  color: var(--text-secondary);
  cursor: pointer;
  transition:
    background 0.15s ease,
    color 0.15s ease;

  &:hover {
    background: var(--button-color);
    color: var(--text-primary);
  }

  /* Wrap the visible text in typographic quotes so the chip reads as a
     literal user phrase even out of context. */
  &::before {
    content: '“';
  }
  &::after {
    content: '”';
  }
`;

// Floating "Copied" toast that pops above a chip on click. Absolutely
// positioned so the chip's width never shifts.
const CopiedToast = styled.span<{ $visible: boolean }>`
  position: absolute;
  bottom: calc(100% + 4px);
  left: 50%;
  transform: translateX(-50%) translateY(${(p) => (p.$visible ? '0' : '4px')});
  padding: 3px 8px;
  border-radius: 6px;
  background: var(--bg-color-page);
  border: 1px solid var(--border-color);
  color: var(--text-primary);
  font-size: 11px;
  font-weight: 500;
  line-height: 1;
  white-space: nowrap;
  pointer-events: none;
  opacity: ${(p) => (p.$visible ? 1 : 0)};
  transition:
    opacity 0.15s ease,
    transform 0.15s ease;
`;

const DisabledBadge = styled.span`
  flex-shrink: 0;
  font-size: 0.625rem;
  font-weight: 500;
  letter-spacing: 0.03em;
  text-transform: uppercase;
  padding: 2px 8px;
  border-radius: 999px;
  border: 1px solid var(--border-color);
  color: var(--text-secondary);
`;

const DisabledReason = styled.div`
  font-size: 0.75rem;
  color: var(--text-secondary);
  line-height: 1.5;
  padding: 8px 10px;
  border-radius: 6px;
  border: 1px solid var(--border-color);
  background: color-mix(in srgb, var(--bg-color-container) 60%, transparent);
`;

const StatusText = styled.div`
  padding: 24px 0;
  text-align: center;
  color: var(--text-secondary);
  font-size: 12px;
`;

const Notice = styled.div`
  padding: 10px 12px;
  border-radius: 8px;
  background: color-mix(in srgb, var(--bg-color-container) 60%, transparent);
  border: 1px solid var(--border-color);
  color: var(--text-secondary);
  font-size: 12px;
`;
